import type { DomainCatalog, DomainEntry } from './types.ts';
import { KIND_META } from './types.ts';

/**
 * The read-only slice of `DomainCatalog` `validateRendered` needs.
 * Derived from the public catalog shape so the entries+order names
 * stay in lockstep with the SSOT — `validate(rendered)` on a
 * `DomainCatalog` value satisfies this contract directly.
 */
export type CatalogView = Pick<DomainCatalog, 'entries' | 'order'>;

/** Returns a human-readable string for an entry, used in error messages. */
function describe(entry: DomainEntry): string {
  return `${KIND_META[entry.kind].heading} "${entry.label}"`;
}

/** Count non-overlapping occurrences of `needle` in `haystack`. */
function count(haystack: string, needle: string): number {
  return needle === '' ? 0 : haystack.split(needle).length - 1;
}

/**
 * Walk the catalog after `renderInstructions` has produced the markdown
 * and assert invariants by scanning the rendered text. Aggregates every
 * failure into one `Error` so the author sees the full picture instead
 * of one error at a time.
 *
 *   - Every non-hidden entry must have its body rendered EXACTLY once
 *     via `<Ref.Def/>` (signature: the line-anchored `### <label>`
 *     heading).
 *   - Every non-hidden entry must also be mentioned at least once
 *     somewhere else — via `<Ref/>` or `{Foo.label}` interpolation,
 *     both of which emit the label text verbatim.
 *
 * Mentions are textual, so a prose occurrence of the exact label also
 * counts — harmless overcounting; the checks only need a lower bound.
 * Label uniqueness and substring-freedom are enforced at registration
 * (see `createDomainCatalog`), which is what keeps this counting
 * unambiguous.
 *
 * Test-only — production never calls this.
 */
export function validateRendered(view: CatalogView, rendered: string): void {
  // Prefix a newline so the line-anchored heading signature matches the
  // very first line of the document too.
  const haystack = `\n${rendered}`;

  const errors = [...view.entries.values()].flatMap((entry) => {
    if (entry.config.hidden) {
      return [];
    }

    // Def rendering: `<h3>` becomes `### <label>` on its own line.
    const defHeader = `\n### ${entry.label}`;
    const defCount = count(haystack, defHeader);
    if (defCount === 0) {
      return [`${describe(entry)}: registered but never rendered with <Ref.Def/>.`];
    }
    if (defCount > 1) {
      return [`${describe(entry)}: body rendered ${defCount} times — must be exactly once.`];
    }

    // The Def heading contains the label exactly once — subtract it to
    // get the bare mention count.
    const mentionCount = count(haystack, entry.label) - defCount;
    if (mentionCount === 0) {
      return [`${describe(entry)}: registered but never referenced via <Ref/> — dead catalog entry.`];
    }
    return [];
  });

  if (errors.length > 0) {
    throw new Error(`DomainCatalog validation failed:\n  - ${errors.join('\n  - ')}`);
  }
}
